import type { ErrorInfo } from 'react';

interface ErrorContext {
  user?: {
    id?: string;
    email?: string;
  };
  extra?: Record<string, unknown>;
  tags?: Record<string, string>;
  level?: 'error' | 'warning' | 'info' | 'debug';
}

/**
 * Error reporting service
 * TODO: Integrate with actual error monitoring service (Sentry, LogRocket, etc.)
 */
class ErrorReportingService {
  private isEnabled: boolean;
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
    this.isEnabled = !this.isDevelopment; // Only enable in production by default
  }

  /**
   * Initialize error reporting service
   * Call this in your app initialization
   */
  init(config?: { enabled?: boolean; environment?: string }) {
    if (config?.enabled !== undefined) {
      this.isEnabled = config.enabled;
    }

    // TODO: Initialize your error monitoring service here
    // Example with Sentry:
    // Sentry.init({
    //   dsn: import.meta.env.VITE_SENTRY_DSN,
    //   environment: config?.environment || import.meta.env.MODE,
    //   integrations: [new BrowserTracing()],
    //   tracesSampleRate: 1.0,
    // });

    if (this.isDevelopment) {
      console.warn('Error reporting service initialized');
    }
  }

  /**
   * Report JavaScript errors
   */
  captureException(error: Error, context?: ErrorContext) {
    if (!this.isEnabled) {
      this.logToConsole('Error captured:', error, context);
      return;
    }

    // TODO: Send to your error monitoring service
    // Example with Sentry:
    // Sentry.captureException(error, {
    //   user: context?.user,
    //   extra: context?.extra,
    //   tags: context?.tags,
    //   level: context?.level || 'error',
    // });

    // Fallback: log to console in development
    if (this.isDevelopment) {
      this.logToConsole('Error captured:', error, context);
    }
  }

  /**
   * Report React component errors
   */
  captureReactError(
    error: Error,
    errorInfo: ErrorInfo,
    context?: ErrorContext
  ) {
    const enhancedContext = {
      ...context,
      extra: {
        ...context?.extra,
        reactErrorInfo: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        ...context?.tags,
        errorType: 'react-error-boundary',
      },
    };

    this.captureException(error, enhancedContext);
  }

  /**
   * Report custom messages
   */
  captureMessage(message: string, context?: ErrorContext) {
    if (!this.isEnabled) {
      this.logToConsole('Message captured:', message, context);
      return;
    }

    // TODO: Send to your error monitoring service
    // Example with Sentry:
    // Sentry.captureMessage(message, {
    //   user: context?.user,
    //   extra: context?.extra,
    //   tags: context?.tags,
    //   level: context?.level || 'info',
    // });

    if (this.isDevelopment) {
      this.logToConsole('Message captured:', message, context);
    }
  }

  /**
   * Set user context for error reporting
   */
  setUserContext(_user: {
    id?: string;
    email?: string;
    [key: string]: unknown;
  }) {
    if (!this.isEnabled) return;

    // TODO: Set user context in your error monitoring service
    // Example with Sentry:
    // Sentry.setUser(user);
  }

  /**
   * Add breadcrumb for debugging
   */
  addBreadcrumb(
    _message: string,
    _category?: string,
    _data?: Record<string, unknown>
  ) {
    if (!this.isEnabled) return;

    // TODO: Add breadcrumb to your error monitoring service
    // Example with Sentry:
    // Sentry.addBreadcrumb({
    //   message,
    //   category: category || 'manual',
    //   data,
    //   level: 'info',
    // });
  }

  /**
   * Development-only console logging
   */
  private logToConsole(
    type: string,
    error: Error | string,
    context?: ErrorContext
  ) {
    if (!this.isDevelopment) return;

    console.error(`🚨 ${type}`, error);
    if (context) {
      console.error('Context:', context);
    }
  }
}

// Export singleton instance
export const errorReporting = new ErrorReportingService();

// Convenience functions
export const reportError = (error: Error, context?: ErrorContext) => {
  errorReporting.captureException(error, context);
};

export const reportMessage = (message: string, context?: ErrorContext) => {
  errorReporting.captureMessage(message, context);
};

export const setErrorUser = (user: {
  id?: string;
  email?: string;
  [key: string]: unknown;
}) => {
  errorReporting.setUserContext(user);
};

export const addErrorBreadcrumb = (
  _message: string,
  _category?: string,
  _data?: Record<string, unknown>
) => {
  errorReporting.addBreadcrumb(_message, _category, _data);
};
